import { PERSONA_SCRIPT_MAX_HOOKS } from "@nexus/contracts";
import { Badge, Button, Icon, Textarea, cn, type IconName } from "@nexus/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  PersonaScriptDto,
  PersonaScriptHookDraft,
  PersonaScriptHookDto,
} from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";
import { useReplacePersonaScriptHooks } from "@/lib/queries";

export interface PersonaScriptHooksSectionProps {
  script: PersonaScriptDto;
  setId: string;
  tenantId: string | null;
  canEdit: boolean;
  /** False for legacy scripts that already carry separate hook-variant scripts. */
  canAddAlternates: boolean;
  /** True while an order line is open: the hook set is frozen, text stays editable. */
  isLocked: boolean;
  previewHookKey: string | null;
  onPreview: (hookKey: string | null) => void;
  surface?: "card" | "drawer";
}

type EditTarget = { kind: "existing"; index: number } | { kind: "new" };

function toDraft(hook: PersonaScriptHookDto): PersonaScriptHookDraft {
  return {
    hookKey: hook.hookKey,
    voiceover: hook.voiceover,
    onScreenText: hook.onScreenText,
    line: hook.line,
    strategyLabel: hook.strategyLabel,
  };
}

function moved<T>(items: T[], from: number, to: number): T[] {
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

/** Opening hooks above the body: one primary that Scene 1 shows plus up to three alternates. */
export function PersonaScriptHooksSection({
  script,
  setId,
  tenantId,
  canEdit,
  canAddAlternates,
  isLocked,
  previewHookKey,
  onPreview,
  surface = "card",
}: PersonaScriptHooksSectionProps) {
  const { t } = useTranslation();
  const replaceHooks = useReplacePersonaScriptHooks();
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [voiceoverDraft, setVoiceoverDraft] = useState("");
  const [onScreenDraft, setOnScreenDraft] = useState("");

  const hooks = script.hooks ?? [];
  if (hooks.length === 0) return null;

  const isSaving = replaceHooks.isPending;
  const canChangeStructure = canEdit && !isLocked && !isSaving;
  const canAdd = canChangeStructure && canAddAlternates && hooks.length < PERSONA_SCRIPT_MAX_HOOKS;
  const drafts = hooks.map(toDraft);

  const save = (next: PersonaScriptHookDraft[]) => {
    if (!tenantId) return;
    replaceHooks.mutate(
      { setId, scriptId: script.id, tenantId, expectedVersion: script.versionCount, hooks: next },
      { onSuccess: () => setEditing(null) },
    );
  };

  const startEdit = (target: EditTarget) => {
    const hook = target.kind === "existing" ? hooks[target.index] : undefined;
    setVoiceoverDraft(hook?.voiceover ?? "");
    setOnScreenDraft(hook?.onScreenText ?? "");
    setEditing(target);
  };

  const commitEdit = () => {
    if (!editing || voiceoverDraft.trim().length === 0) return;
    const onScreenText = onScreenDraft || null;
    if (editing.kind === "new") {
      save([...drafts, { voiceover: voiceoverDraft, onScreenText, line: null }]);
      return;
    }
    const current = drafts[editing.index]!;
    // A null line follows the voiceover (the primary's becomes the script title), so it is cleared
    // only when the voiceover itself changed; an on-screen-only edit keeps the existing line.
    const isVoiceoverChanged = voiceoverDraft.trim() !== current.voiceover.trim();
    const edited = {
      voiceover: voiceoverDraft,
      onScreenText,
      line: isVoiceoverChanged ? null : current.line,
    };
    save(drafts.map((draft, index) => (index === editing.index ? { ...draft, ...edited } : draft)));
  };

  const editingIndex = editing?.kind === "existing" ? editing.index : null;
  const heading = surface === "drawer"
    ? "text-caption font-medium tracking-label text-muted-foreground uppercase"
    : "text-[11px] font-medium uppercase tracking-wide text-muted-foreground";

  const editor = (
    <div className="space-y-2" onClick={(event) => event.stopPropagation()}>
      <Textarea
        value={voiceoverDraft}
        rows={2}
        disabled={isSaving}
        onChange={(event) => setVoiceoverDraft(event.target.value)}
        aria-label={t("personaOpportunities.scripts.hooks.voiceoverAria", "Hook voiceover")}
        placeholder={t("personaOpportunities.scripts.hooks.voiceoverPlaceholder", "What the creator says first")}
      />
      <Textarea
        value={onScreenDraft}
        rows={1}
        disabled={isSaving}
        onChange={(event) => setOnScreenDraft(event.target.value)}
        aria-label={t("personaOpportunities.scripts.hooks.onScreenAria", "On-screen text")}
        placeholder={t("personaOpportunities.scripts.hooks.onScreenPlaceholder", "On-screen text (optional)")}
      />
      {voiceoverDraft.trim().length === 0 ? (
        <p className="text-xs text-destructive">
          {t("personaOpportunities.scripts.hooks.voiceoverRequired", "A hook needs a voiceover")}
        </p>
      ) : null}
      <div className="flex gap-1">
        <Button
          type="button"
          size="xs"
          disabled={isSaving || voiceoverDraft.trim().length === 0}
          onClick={commitEdit}
        >
          {t("personaOpportunities.scripts.cell.save", "Save")}
        </Button>
        <Button type="button" size="xs" variant="secondary" disabled={isSaving} onClick={() => setEditing(null)}>
          {t("personaOpportunities.scripts.cell.cancel", "Cancel")}
        </Button>
      </div>
    </div>
  );

  const action = (
    icon: IconName,
    label: string,
    onClick: () => void,
    disabled = false,
  ) => (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-7 min-h-11 min-w-11 text-muted-foreground md:min-h-7 md:min-w-7"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      <Icon name={icon} color="inherit" size="xs" />
    </Button>
  );

  return (
    <section className="space-y-2" aria-label={t("personaOpportunities.scripts.hooks.title", "Hooks")}>
      <div className="flex flex-wrap items-center gap-2">
        <h4 className={heading}>{t("personaOpportunities.scripts.hooks.title", "Hooks")}</h4>
        {isLocked ? (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Icon name="lock" color="inherit" size="xs" aria-hidden />
            {t(
              "personaOpportunities.scripts.hooks.locked",
              "In production — hooks can be edited but not added, removed or reordered",
            )}
          </span>
        ) : null}
      </div>
      <ol className="space-y-2">
        {hooks.map((hook, index) => {
          const isPrimary = index === 0;
          const isPreviewing = previewHookKey === hook.hookKey;
          return (
            <li
              key={hook.hookKey}
              className={cn(
                "rounded-sm border bg-background px-3 py-2.5",
                isPreviewing ? "border-[1.5px] border-primary" : "border-border",
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={isPrimary ? "slate" : "default"}>
                  {isPrimary
                    ? t("personaOpportunities.scripts.hooks.primary", "Primary")
                    : t("personaOpportunities.scripts.hooks.alternate", {
                        defaultValue: "Alternate {{number}}",
                        number: index,
                      })}
                </Badge>
                {hook.strategyLabel ? (
                  <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {hook.strategyLabel}
                  </span>
                ) : null}
                {isPreviewing ? (
                  <span className="text-xs font-medium text-primary">
                    {t("personaOpportunities.scripts.hooks.previewing", "Previewing in scene 1")}
                  </span>
                ) : null}
                <div className="ml-auto flex items-center">
                  {!isPrimary
                    ? action(
                        isPreviewing ? "eye-off" : "eye",
                        isPreviewing
                          ? t("personaOpportunities.scripts.hooks.stopPreview", "Show the primary in scene 1")
                          : t("personaOpportunities.scripts.hooks.preview", "Preview in scene 1"),
                        () => onPreview(isPreviewing ? null : hook.hookKey),
                      )
                    : null}
                  {canEdit
                    ? action("pencil", t("personaOpportunities.scripts.hooks.edit", "Edit hook"), () =>
                        startEdit({ kind: "existing", index }),
                      isSaving)
                    : null}
                  {canEdit && !isPrimary
                    ? action(
                        "star",
                        t("personaOpportunities.scripts.hooks.makePrimary", "Make primary"),
                        () => save(moved(drafts, index, 0)),
                        !canChangeStructure,
                      )
                    : null}
                  {canEdit
                    ? action(
                        "arrow-up",
                        t("personaOpportunities.scripts.hooks.moveUp", "Move up"),
                        () => save(moved(drafts, index, index - 1)),
                        !canChangeStructure || index === 0,
                      )
                    : null}
                  {canEdit
                    ? action(
                        "arrow-down",
                        t("personaOpportunities.scripts.hooks.moveDown", "Move down"),
                        () => save(moved(drafts, index, index + 1)),
                        !canChangeStructure || index === hooks.length - 1,
                      )
                    : null}
                  {canEdit && hooks.length > 1
                    ? action(
                        "trash-2",
                        t("personaOpportunities.scripts.hooks.remove", "Remove hook"),
                        () => save(drafts.filter((_, draftIndex) => draftIndex !== index)),
                        !canChangeStructure,
                      )
                    : null}
                </div>
              </div>
              {editingIndex === index ? (
                <div className="mt-2">{editor}</div>
              ) : (
                <div className="mt-1.5 space-y-1">
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{hook.voiceover}</p>
                  {hook.onScreenText ? (
                    <p className="text-xs text-muted-foreground">
                      {t("personaOpportunities.scripts.hooks.onScreen", {
                        defaultValue: "On screen: {{text}}",
                        text: hook.onScreenText,
                      })}
                    </p>
                  ) : null}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {editing?.kind === "new" ? (
        <div className="rounded-sm border border-dashed border-border px-3 py-2.5">{editor}</div>
      ) : canAdd ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          iconStart="plus"
          onClick={(event) => {
            event.stopPropagation();
            startEdit({ kind: "new" });
          }}
        >
          {t("personaOpportunities.scripts.hooks.add", "Add hook")}
        </Button>
      ) : null}
    </section>
  );
}
